// src/components/OnboardingTour.jsx
//
// Tour de onboarding (6 pasos) con react-joyride. Diseño consistente con el
// resto del dashboard (vars CSS --accent / --ink / --muted, tipografía display).
//
// API:
//   <OnboardingTour run={showTour} onFinish={() => setShowTour(false)} />
//
// Persistencia:
//   - Al terminar / saltar → localStorage.setItem('edgeelite_tour_completed','true')
//   - El componente padre decide cuándo lanzarlo:
//       useEffect(() => {
//         if (!localStorage.getItem('edgeelite_tour_completed')) setShowTour(true);
//       }, []);
//   - El item "Volver a ver el recorrido" del Sidebar borra la flag y vuelve
//     a setShowTour(true).
//
// Mobile:
//   - El paso "Aprende" (target = sidebar) cae a body/center con copy
//     adaptado, porque en mobile el sidebar es drawer cerrado por defecto.

import React from 'react';
// react-joyride 3.x exporta nombres (no default).
import { Joyride, STATUS } from 'react-joyride';
import { useDashboardTheme } from '../theme/DashboardTheme';

const TOUR_STORAGE_KEY = 'edgeelite_tour_completed';

// ── Tooltip custom (reemplaza el default de joyride) ──────────────────────
//
// IMPORTANTE: cada botón terminal (close/skip/¡Listo!) guarda la flag
// localStorage MANUALMENTE y llama onFinishParent también manualmente,
// como DEFENSA REDUNDANTE por si el callback de Joyride no dispara
// `tour:end` con timing previsible (caso observado en producción).
// La fuente de verdad pasa a ser el click en el botón, no el callback.
function CustomTooltip(props) {
  const {
    backProps,
    closeProps,
    continuous,
    index,
    primaryProps,
    skipProps,
    step,
    tooltipProps,
    size,
    isLastStep,
  } = props;

  const stepNumber = index + 1;

  // onFinishParent viene desde OnboardingTour vía step.data.onFinishParent
  // (forzamos que llegue a CADA step para garantizar disponibilidad).
  const onFinishParent = step.data && step.data.onFinishParent;

  function persistAndNotify() {
    try { localStorage.setItem(TOUR_STORAGE_KEY, 'true'); } catch { /* ignore */ }
    if (typeof onFinishParent === 'function') onFinishParent();
  }

  function wrappedClose(e) {
    persistAndNotify();
    if (closeProps && closeProps.onClick) closeProps.onClick(e);
  }
  function wrappedSkip(e) {
    persistAndNotify();
    if (skipProps && skipProps.onClick) skipProps.onClick(e);
  }
  function wrappedPrimary(e) {
    if (isLastStep) persistAndNotify();
    if (primaryProps && primaryProps.onClick) primaryProps.onClick(e);
  }

  return (
    <div
      {...tooltipProps}
      style={{
        // Tokens de tema → bg-elev cambia automáticamente light ↔ dark.
        background: 'var(--bg-elev)',
        border: '1px solid var(--line)',
        borderRadius: 14,
        padding: 24,
        width: 360,
        maxWidth: '90vw',
        // Shadow más intenso para que se note igual sobre fondos oscuros.
        boxShadow: '0 24px 60px rgba(0, 0, 0, 0.45)',
        color: 'var(--ink)',
        fontFamily: 'inherit',
      }}
    >
      {/* Counter pill + close */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '.06em',
            textTransform: 'uppercase',
            color: 'var(--accent)',
            background: 'var(--accent-soft, color-mix(in oklab, var(--accent) 12%, transparent))',
            padding: '4px 10px',
            borderRadius: 999,
          }}
        >
          Paso {stepNumber} de {size}
        </span>
        <button
          {...closeProps}
          onClick={wrappedClose}
          aria-label="Cerrar"
          style={{
            background: 'none',
            border: 0,
            color: 'var(--muted)',
            cursor: 'pointer',
            fontSize: 18,
            lineHeight: 1,
            padding: 4,
          }}
        >
          ×
        </button>
      </div>

      {/* Illustration emoji (si el step lo trae) */}
      {step.data && step.data.illustration && (
        <div style={{ fontSize: 42, lineHeight: 1, marginBottom: 12, textAlign: 'center' }} aria-hidden="true">
          {step.data.illustration}
        </div>
      )}

      {/* Title */}
      {step.title && (
        <h3
          style={{
            fontFamily: 'var(--font-display, inherit)',
            fontSize: 18,
            fontWeight: 700,
            color: 'var(--ink)',
            margin: 0,
            marginBottom: 8,
            letterSpacing: '-0.01em',
            lineHeight: 1.25,
          }}
        >
          {step.title}
        </h3>
      )}

      {/* Content */}
      <div
        style={{
          fontSize: 13,
          color: 'var(--muted)',
          lineHeight: 1.55,
          marginBottom: 18,
        }}
      >
        {step.content}
      </div>

      {/* Progress dots */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginBottom: 14 }}>
        {Array.from({ length: size }).map((_, i) => {
          const active = i === index;
          return (
            <span
              key={i}
              aria-hidden="true"
              style={{
                width: active ? 18 : 6,
                height: 6,
                borderRadius: active ? 3 : '50%',
                background: active ? 'var(--accent)' : 'var(--line-strong)',
                transition: 'all .2s',
              }}
            />
          );
        })}
      </div>

      {/* Footer botones */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <button
          {...skipProps}
          onClick={wrappedSkip}
          style={{
            background: 'none',
            border: 0,
            color: 'var(--muted)',
            cursor: 'pointer',
            fontSize: 12.5,
            fontFamily: 'inherit',
            padding: '6px 4px',
          }}
        >
          Saltar recorrido
        </button>

        <div style={{ display: 'flex', gap: 8 }}>
          {index > 0 && (
            <button
              {...backProps}
              style={{
                background: 'transparent',
                border: '1px solid var(--line)',
                color: 'var(--ink)',
                borderRadius: 8,
                padding: '8px 14px',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              Anterior
            </button>
          )}
          {continuous && (
            <button
              {...primaryProps}
              onClick={wrappedPrimary}
              style={{
                background: 'var(--accent)',
                border: 0,
                color: 'var(--accent-ink, #fff)',
                borderRadius: 8,
                padding: '8px 16px',
                fontSize: 13,
                fontWeight: 700,
                cursor: 'pointer',
                fontFamily: 'inherit',
                letterSpacing: '.02em',
              }}
            >
              {isLastStep ? '¡Listo!' : 'Siguiente'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Steps definition ──────────────────────────────────────────────────────
// 6 pasos enfocados en CÓMO USAR la plataforma:
//   1. Bienvenida (center)
//   2. Motor cuantitativo (MotorInfoCard)
//   3. Partidos en vivo (sección)
//   4. Gráfico del edge (señalamos la primera fila e invitamos a abrirla;
//      el modal con el gráfico es accionable porque spotlightClicks=true)
//   5. Sección Aprende (item del sidebar)
//   6. Cierre: dashboard + Telegram + banca (center)
const ALL_STEPS = [
  {
    target: 'body',
    placement: 'center',
    title: 'Bienvenido a Edge Elite',
    content: 'Te mostramos cómo leer la plataforma en menos de un minuto. Si ya la conoces, puedes saltarlo.',
    data: { illustration: '👋' },
    disableBeacon: true,
  },
  {
    target: '[data-tour="motor-card"]',
    title: 'El motor cuantitativo',
    content: 'Es el cerebro de Edge Elite. Analiza todos los partidos en vivo y calcula dónde hay ventaja, en tiempo real.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour="games-section"]',
    title: 'Los partidos en vivo',
    content: 'Aquí ves todos los partidos por liga. Toca cualquiera para abrir su análisis en detalle.',
    placement: 'top',
    disableBeacon: true,
  },
  {
    target: '[data-tour="game-row"]',
    title: 'El gráfico es lo más importante',
    content: 'Al abrir un partido ves el gráfico del edge en vivo. Cuando el edge llega a su umbral dentro de una ventana válida, es tu oportunidad de entrada. Aquí está el corazón de todo.',
    placement: 'top',
    disableBeacon: true,
  },
  {
    target: '[data-tour="tour-aprende"]',
    title: 'Aprende a usar Edge Elite',
    content: 'Lo más importante: en la sección Aprende tienes el tutorial en video y la guía oficial paso a paso. Empieza por ahí.',
    placement: 'right',
    disableBeacon: true,
    data: { illustration: '📚' },
    _sidebarOnly: true,
    _mobileFallback: {
      target: 'body',
      placement: 'center',
      content: 'Lo más importante: abre el menú lateral (☰) y entra a la sección Aprende. Ahí tienes el tutorial en video y la guía oficial paso a paso. Empieza por ahí.',
    },
  },
  {
    target: 'body',
    placement: 'center',
    title: '🎯 Así se construyen las ganancias',
    content: 'Aprende a leer el dashboard y combínalo con las alertas de Telegram: juntas son tus dos fuentes válidas. En conjunto se construyen las ganancias. Y siempre respeta tu banca: 1-2% por pick.',
    data: { illustration: '🎯' },
    disableBeacon: true,
  },
];

function getStepsForViewport() {
  // En mobile el sidebar es drawer cerrado por default → los pasos con
  // target en el sidebar usan su _mobileFallback (body / center + copy
  // adaptado), así el usuario igual recibe la información sin que el
  // tooltip apunte a un elemento invisible.
  const isMobile = typeof window !== 'undefined'
    && window.matchMedia
    && window.matchMedia('(max-width: 768px)').matches;
  if (!isMobile) return ALL_STEPS;
  return ALL_STEPS.map(s => {
    if (s._sidebarOnly && s._mobileFallback) {
      return { ...s, ...s._mobileFallback };
    }
    return s;
  });
}

// Locale memoizable a nivel módulo — nunca cambia, así no genera nuevos refs
// en cada render del padre.
const TOUR_LOCALE = Object.freeze({
  back: 'Anterior',
  close: 'Cerrar',
  last: '¡Listo!',
  next: 'Siguiente',
  skip: 'Saltar recorrido',
});

// Estilos dependientes del tema. Joyride inyecta arrow/overlay/spotlight como
// CSS inline en el DOM (no resuelve var(--…)), así que los hex se calculan en
// runtime según el theme actual del dashboard. El resto del tooltip vive en
// CustomTooltip y SÍ usa var(--…) directamente.
//
// NOTA: removida la sección `spotlight` porque pasaba props inválidos al DOM
// en react-joyride 3.x (warning "borderradius is not a standard DOM attribute").
// El border accent del spotlight se aplica vía CSS global en index.css con la
// clase `.react-joyride__spotlight`.

// Hex equivalentes a var(--bg-elev) en cada modo, para los arrows del tooltip
// (joyride no resuelve CSS vars). Mantener en sync con index.css:
//   :root          { --bg-elev: #FFFFFF }
//   [data-theme=dark] { --bg-elev: #1C1C1E }
const BG_ELEV_DARK  = '#1C1C1E';
const BG_ELEV_LIGHT = '#FFFFFF';

function OnboardingTour({ run, onFinish }) {
  const { theme } = useDashboardTheme();
  const isDark = theme === 'dark';

  const tourStyles = React.useMemo(() => ({
    options: {
      zIndex: 10000,
      arrowColor: isDark ? BG_ELEV_DARK : BG_ELEV_LIGHT,
      overlayColor: isDark ? 'rgba(0, 0, 0, 0.72)' : 'rgba(0, 0, 0, 0.55)',
      primaryColor: '#FF6B2C',
    },
  }), [isDark]);

  const tourFloaterProps = React.useMemo(() => ({
    styles: { arrow: { color: isDark ? BG_ELEV_DARK : BG_ELEV_LIGHT } },
  }), [isDark]);

  // Inyectamos onFinish en step.data.onFinishParent para que el CustomTooltip
  // pueda llamarlo MANUALMENTE al cerrar (defensa por si el callback de
  // Joyride no dispara). useMemo con dep [onFinish] mantiene referencia estable.
  const steps = React.useMemo(() => {
    const baseSteps = getStepsForViewport();
    return baseSteps.map(s => ({
      ...s,
      data: { ...(s.data || {}), onFinishParent: onFinish },
    }));
  }, [onFinish]);

  // Callback secundario (la fuente de verdad pasa a ser el click en el
  // tooltip, no el callback). Igualmente lo dejamos por si Joyride sí
  // dispara y queremos cerrar el tour de forma centralizada.
  const handleCallback = React.useCallback((data) => {
    const { status, action, type } = data;
    const isTerminal =
      type === 'tour:end' ||
      action === 'close' ||
      action === 'skip' ||
      [STATUS.FINISHED, STATUS.SKIPPED].includes(status);
    if (isTerminal) {
      try { localStorage.setItem(TOUR_STORAGE_KEY, 'true'); } catch { /* ignore */ }
      if (onFinish) onFinish();
    }
  }, [onFinish]);

  if (!run) return null;

  return (
    <Joyride
      steps={steps}
      run={run}
      continuous
      showSkipButton
      disableOverlayClose
      hideCloseButton={false}
      scrollToFirstStep
      scrollOffset={80}
      tooltipComponent={CustomTooltip}
      callback={handleCallback}
      locale={TOUR_LOCALE}
      styles={tourStyles}
      floaterProps={tourFloaterProps}
      /* 2026-05-10: spotlightClicks=true permite click en el spotlight target,
         disableOverlay=true elimina el overlay que cubre toda la página y
         bloqueaba clicks en sidebar / nav. El tour sigue mostrando el tooltip
         + spotlight ring sin oscurecer el resto, así el usuario puede navegar
         libremente mientras el tour está activo (con la flag de "ya entendí"
         persistida cuando avanza o cierra). */
      disableOverlay
      spotlightClicks
    />
  );
}

// React.memo: bloquea re-renders del wrapper si run/onFinish no cambian.
// Combinado con useCallback en el padre para onFinish, garantiza que
// Joyride no se reinicie cuando el padre cambia state ajeno (page/filter).
export default React.memo(OnboardingTour);

export { TOUR_STORAGE_KEY };
